/**
 * Contact provenance and review (TRUS-770).
 *
 * Lead Builder now finds CBO and clinic leaders in public records (NPPES,
 * IRS Form 990) and on the organization's own website before any paid
 * provider. Each contact carries where it came from, whether its email
 * address can be sent to, and whether a person still has to approve it.
 * These pieces show that on a contact row and in the Review tab.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, ClipboardCheck, ExternalLink, ShieldCheck, XCircle } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { leadBuilderApi, type ContactReviewItem, type DiscoveredContact } from '../../../shared/services/platformApi';

type EmailStatus = DiscoveredContact['email_status'];

const EMAIL_STATUS_STYLE: Record<string, string> = {
  verified: 'bg-green-100 text-green-700',
  published: 'bg-green-100 text-green-700',
  risky: 'bg-amber-100 text-amber-700',
  guess: 'bg-gray-200 text-gray-600',
  invalid: 'bg-red-100 text-red-700',
};

function useLabels() {
  const { t } = useAppTranslation();
  return {
    emailStatus: (s: string) =>
      ({
        verified: t('platform.leadBuilder.provenance.email.verified', 'Email verified'),
        published: t('platform.leadBuilder.provenance.email.published', 'Email on their website'),
        risky: t('platform.leadBuilder.provenance.email.risky', 'Email unconfirmed (catch-all)'),
        guess: t('platform.leadBuilder.provenance.email.guess', 'Email guessed'),
        invalid: t('platform.leadBuilder.provenance.email.invalid', 'Email invalid'),
      })[s] || s,
    source: (s?: string | null) =>
      ({
        nppes_authorized_official: t('platform.leadBuilder.provenance.source.nppes', 'NPPES'),
        irs_990: t('platform.leadBuilder.provenance.source.irs990', 'IRS 990'),
        irs_990_principal_officer: t('platform.leadBuilder.provenance.source.irs990', 'IRS 990'),
        org_website: t('platform.leadBuilder.provenance.source.website', 'Their website'),
        hunter_io: 'Hunter',
        apollo_io: 'Apollo',
        provider_directory: t('platform.leadBuilder.provenance.source.directory', 'Directory'),
        web_scrape: t('platform.leadBuilder.provenance.source.website', 'Their website'),
      })[s || ''] || s || '',
    role: (r?: string | null) =>
      ({
        executive: t('platform.leadBuilder.provenance.role.executive', 'Executive'),
        medicaid_ecm: t('platform.leadBuilder.provenance.role.medicaidEcm', 'Medicaid / ECM'),
        program: t('platform.leadBuilder.provenance.role.program', 'Program'),
        clinical: t('platform.leadBuilder.provenance.role.clinical', 'Clinical'),
        finance: t('platform.leadBuilder.provenance.role.finance', 'Finance'),
        governance: t('platform.leadBuilder.provenance.role.board', 'Board'),
        manager: t('platform.leadBuilder.provenance.role.manager', 'Manager'),
      })[r || ''] || '',
    confirmed: t('platform.leadBuilder.provenance.confirmed', 'Confirmed on their website'),
    stale: t('platform.leadBuilder.provenance.stale', 'Not on their website now'),
    needsReview: t('platform.leadBuilder.provenance.needsReview', 'Needs review'),
    rejected: t('platform.leadBuilder.provenance.rejected', 'Rejected'),
    asOf: (v: string) => t('platform.leadBuilder.provenance.asOf', 'as of {{date}}', { date: v }),
    approve: t('platform.leadBuilder.provenance.approve', 'Approve'),
    reject: t('platform.leadBuilder.provenance.reject', 'Reject'),
  };
}

interface BadgeSource {
  role_class?: string | null;
  email?: string | null;
  email_status?: EmailStatus;
  data_source?: string;
  evidence?: string | null;
  evidence_url?: string | null;
  source_as_of?: string | null;
  confirmed_on_site?: boolean;
  possibly_stale?: boolean;
  review_status?: string | null;
}

/** Role, email state, source link and review state for one contact. */
export function ContactProvenanceBadges({ contact }: { contact: BadgeSource }) {
  const L = useLabels();
  const role = L.role(contact.role_class);
  const status = contact.email ? contact.email_status : null;
  return (
    <div className="flex flex-wrap items-center gap-1.5 mt-1">
      {role && <span className="px-1.5 py-0.5 text-[10px] font-medium bg-blue-50 text-blue-700 rounded">{role}</span>}
      {status && (
        <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${EMAIL_STATUS_STYLE[status] || ''}`}>
          {L.emailStatus(status)}
        </span>
      )}
      {contact.confirmed_on_site && (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium bg-green-50 text-green-700 rounded">
          <ShieldCheck className="w-3 h-3" aria-hidden="true" />
          {L.confirmed}
        </span>
      )}
      {contact.possibly_stale && (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium bg-amber-50 text-amber-700 rounded">
          <AlertTriangle className="w-3 h-3" aria-hidden="true" />
          {L.stale}
        </span>
      )}
      {contact.review_status === 'needs_review' && (
        <span className="px-1.5 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-800 rounded">{L.needsReview}</span>
      )}
      {contact.review_status === 'rejected' && (
        <span className="px-1.5 py-0.5 text-[10px] font-medium bg-red-50 text-red-700 rounded">{L.rejected}</span>
      )}
      {contact.data_source &&
        (contact.evidence_url ? (
          <a
            href={contact.evidence_url}
            target="_blank"
            rel="noopener noreferrer"
            title={contact.evidence || undefined}
            className="inline-flex items-center gap-0.5 text-[10px] text-gray-500 hover:text-blue-600 underline"
          >
            {L.source(contact.data_source)}
            {contact.source_as_of ? ` · ${L.asOf(contact.source_as_of)}` : ''}
            <ExternalLink className="w-3 h-3" aria-hidden="true" />
          </a>
        ) : (
          <span className="text-[10px] text-gray-400" title={contact.evidence || undefined}>
            {L.source(contact.data_source)}
          </span>
        ))}
    </div>
  );
}

/** Approve / reject buttons; calls onDone with the new review status. */
export function ContactReviewActions({
  contactId,
  onDone,
}: {
  contactId: string;
  onDone: (status: 'approved' | 'rejected') => void;
}) {
  const L = useLabels();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const decide = async (decision: 'approved' | 'rejected') => {
    setBusy(true);
    setError(null);
    const res = await leadBuilderApi.reviewContact(contactId, decision);
    setBusy(false);
    if (res.success) onDone(decision);
    else setError(res.error?.message || 'Could not save the review. Try again.');
  };
  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        disabled={busy}
        onClick={() => decide('approved')}
        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded disabled:opacity-50"
      >
        <CheckCircle2 className="w-3.5 h-3.5" aria-hidden="true" />
        {L.approve}
      </button>
      <button
        type="button"
        disabled={busy}
        onClick={() => decide('rejected')}
        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded disabled:opacity-50"
      >
        <XCircle className="w-3.5 h-3.5" aria-hidden="true" />
        {L.reject}
      </button>
      {error && <span className="text-[10px] text-red-600 ml-1">{error}</span>}
    </div>
  );
}

/** Review tab: every contact waiting on a person, across companies. */
export function ContactReviewTab({ onCountChange }: { onCountChange?: (n: number) => void }) {
  const { t } = useAppTranslation();
  const [items, setItems] = useState<ContactReviewItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await leadBuilderApi.getContactReviewQueue(200);
    setLoading(false);
    if (res.success && res.data) {
      setItems(res.data.items);
      onCountChange?.(res.data.items.length);
    } else {
      setError(res.error?.message || t('platform.leadBuilder.review.loadError', 'Could not load the review queue.'));
    }
  }, [onCountChange, t]);

  useEffect(() => {
    load();
  }, [load]);

  const remove = (id: string) =>
    setItems((prev) => {
      const next = prev.filter((i) => i.id !== id);
      onCountChange?.(next.length);
      return next;
    });

  if (loading) {
    return <p className="text-sm text-gray-500">{t('platform.leadBuilder.review.loading', 'Loading contacts to review…')}</p>;
  }
  if (error) {
    return (
      <div className="p-4 bg-red-50 text-sm text-red-700 rounded-lg">
        {error}{' '}
        <button type="button" onClick={load} className="underline">
          {t('platform.leadBuilder.review.retry', 'Try again')}
        </button>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <ClipboardCheck className="w-4 h-4" aria-hidden="true" />
          {t('platform.leadBuilder.review.title', 'Contacts to review')}
        </h2>
        <p className="text-xs text-gray-500 mt-1 max-w-3xl">
          {t(
            'platform.leadBuilder.review.help',
            'These people come from public records or have an email address we could not verify. Approve the ones that are right before adding them to a campaign. Rejected contacts and unapproved guessed addresses are never emailed.',
          )}
        </p>
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-gray-500 italic">
          {t('platform.leadBuilder.review.empty', 'Nothing to review. New contacts that need a check will appear here.')}
        </p>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
          {items.map((item) => (
            <div key={item.id} className="flex flex-wrap items-start justify-between gap-3 p-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900">
                  {item.first_name} {item.last_name}
                  {item.company_name && <span className="font-normal text-gray-500"> · {item.company_name}</span>}
                </p>
                <p className="text-xs text-gray-500">
                  {item.title || t('platform.leadBuilder.review.unknownTitle', 'Unknown title')}
                  {item.email ? ` · ${item.email}` : ''}
                  {item.phone ? ` · ${item.phone}` : ''}
                </p>
                <ContactProvenanceBadges contact={{ ...item, review_status: 'needs_review' }} />
              </div>
              <ContactReviewActions contactId={item.id} onDone={() => remove(item.id)} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
